import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { OptionType } from '@features/admin/option-management/models/option-type.enum';
import { OptionModel } from '@features/admin/option-management/models/option.model';
import { optionSelector } from '@features/admin/option-management/store/option.selectors';
import { ProtocolModel } from '@features/protocols/models/protocol.model';
import { ProtocolService } from '@features/protocols/services/protocol.service';
import { ProtocolState } from '@features/protocols/store/protocol.reducers';
import { select, Store } from '@ngrx/store';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { map, Observable } from 'rxjs';

@Component({
  selector: 'app-protocol-history-modal',
  templateUrl: './protocol-history-modal.component.html',
  styleUrls: ['./protocol-history-modal.component.scss']
})
export class ProtocolHistoryModalComponent implements OnInit {
  form = new FormGroup({});
  fields: FormlyFieldConfig[] = [];
  model = {} as ProtocolModel;
  // Dropdown options
  statusOptions$!: Observable<OptionModel[]>;
  investigatorOptions$!: Observable<OptionModel[]>;
  ntrCoordinatorOptions$!: Observable<OptionModel[]>;
  researchTypeOptions$!: Observable<OptionModel[]>;
  departmentOptions$!: Observable<OptionModel[]>;
  schoolOptions$!: Observable<OptionModel[]>;
  institutionOptions$!: Observable<OptionModel[]>;
  collaboratingInstitutionOptions$!: Observable<OptionModel[]>;
  sponsorTypeOptions$!: Observable<OptionModel[]>;
  reviewTypeOptions$!: Observable<OptionModel[]>;
  reviewCycleOptions$!: Observable<OptionModel[]>;
  specialFindingOptions$!: Observable<any[]>; // SubCategoryModel[]
  fundingAgency$!: Observable<OptionModel[]>;
  natureOfResearch$!: Observable<OptionModel[]>;
  sponsoredClinicalTrial$!: Observable<OptionModel[]>;
  irbofRecords$!: Observable<OptionModel[]>;
  cededReviewTypeId?: number;
  sponsoredResearchTypeId?: number;

  constructor(@Inject(MAT_DIALOG_DATA) public data: ProtocolModel, private fb: FormBuilder, private store: Store<ProtocolState>,) { }
  ngOnInit(): void {
    this.initForm(this.data);
    this.initDropdowns(this.data); 
  }

  private initForm(model: ProtocolModel): void {

    const sponsorTypeArray = this.fb.array([]);
    model.sponsorTypeCategories?.forEach((entry, i) => {
      sponsorTypeArray.push(
        this.fb.group({
          categoryId: [entry.categoryId ?? null, Validators.required],
          subCategoryId: [entry.subCategoryId ?? null, Validators.required],
          sponsorProtocol: [entry.sponsorProtocol ?? '', Validators.maxLength(250)],
        })
      );
    });

    this.form = this.fb.group({
      studyTitle: [{ value: model.studyTitle || '', disabled: true }],
      irbNetNumber: [{ value: model.irbNetNumber || '', disabled: true }],
      statusId: [{ value: model.statusId || '', disabled: true }],
      investigatorId: [{ value: model.investigatorId || '', disabled: true }],
      coInvestigator: [{ value: model.coInvestigator || '', disabled: true }],
      ntrIrbCoordinatorId: [{ value: model.ntrIrbCoordinatorId || '', disabled: true }],
      crCoordinator: [{ value: model.crCoordinator || '', disabled: true }],
      typeOfResearchId: [{ value: model.typeOfResearchId || '', disabled: true }],
      departmentId: [{ value: model.departmentId || '', disabled: true }],
      schoolId: [{ value: model.schoolId || '', disabled: true }],
      institutionId: [{ value: model.institutionId || '', disabled: true }],
      collaboratingInstitutionId: [{ value: model.collaboratingInstitutionId || '', disabled: true }],
      sponsorTypeId: [{ value: model.sponsorTypeId || '', disabled: true }],
      sponsor: [{ value: model.sponsor || '', disabled: true }],
      sponsorTypeCategories: sponsorTypeArray,
      sponsorProtocol: [{ value: model.sponsorProtocol || '', disabled: true }],
      typeOfReviewId: [{ value: model.typeOfReviewId || '', disabled: true }],
      reviewCycleId: [{ value: model.reviewCycleId || '', disabled: true }],
      specialFindingCategories: [{ value: model.specialFindingCategories || [], disabled: true }],
      specialFindings: [{ value: model.specialFindings || '', disabled: true }],
      submissionDate: [{ value: model.submissionDate || null, disabled: true }],
      initialReview: [{ value: model.initialReview || null, disabled: true }],
      mostRecentReview: [{ value: model.mostRecentReview || null, disabled: true }],
      initialApprovalDate: [{ value: model.initialApprovalDate || null, disabled: true }],
      approvalThrough: [{ value: model.approvalThrough || null, disabled: true }],
      sendNextReview: [{ value: model.sendNextReview || null, disabled: true }],
      continuingReviewDate: [{ value: model.continuingReviewDate || null, disabled: true }],
      completeDate: [{ value: model.completeDate || null, disabled: true }],
      irbofRecordId: [{ value: model.irbofRecordId || null, disabled: true }],
      fundingAgencyId: [{ value: model.fundingAgencyId || null, disabled: true }],
      natureOfResearchId: [{ value: model.natureOfResearchId || null, disabled: true }],
      sponsoredClinicalTrialId: [{ value: model.sponsoredClinicalTrialId || null, disabled: true }]
    });
  }
    private initDropdowns(model: ProtocolModel): void {
      const get = (type: OptionType, selectedId?: number) =>
        this.store.pipe(
          select(optionSelector.optionsByType(type)),
          map(options => this.appendSelectedOptionIfDisabled(selectedId ?? 0, options))
        );
  
      this.statusOptions$ = get(OptionType.status, model.statusId);
      this.investigatorOptions$ = get(OptionType.investigator, model.investigatorId);
      this.ntrCoordinatorOptions$ = get(OptionType.nTRIRBCoordinator, model.ntrIrbCoordinatorId);
      this.researchTypeOptions$ = get(OptionType.typeOfResearch, model.typeOfResearchId).pipe(
        map(options => {  
          const sponsoredResearch = options.find(o => o.name.toLowerCase() === 'sponsored clinical trial (drug/device/biologic)'); //Sponsored Clinical Trial (Drug/Device/Biologic)
          this.sponsoredResearchTypeId = sponsoredResearch?.id;
          return options;
        }
      ));
      
      
      this.fundingAgency$ = get(OptionType.fundingAgency, this.model.fundingAgencyId);
      this.natureOfResearch$ = get(OptionType.natureOfResearch, this.model.natureOfResearchId);
      this.sponsoredClinicalTrial$ = get(OptionType.sponsoredClinicalTrial, this.model.sponsoredClinicalTrialId);
      this.irbofRecords$ = get(OptionType.irbofRecord, this.model.irbofRecordId);
      this.departmentOptions$ = get(OptionType.department, model.departmentId);
      this.schoolOptions$ = get(OptionType.school, model.schoolId);
      this.institutionOptions$ = get(OptionType.institution, model.institutionId);
      this.collaboratingInstitutionOptions$ = get(OptionType.collaboratingInstitution, model.collaboratingInstitutionId);
      this.sponsorTypeOptions$ = get(OptionType.sponsorType, model.sponsorTypeId);
      this.reviewTypeOptions$ = get(OptionType.typeOfReview, model.typeOfReviewId).pipe(
        map(options => {
          const ceded = options.find(o => o.name.toLowerCase() === 'ceded');
          this.cededReviewTypeId = ceded?.id;
          return options;
        })
      );
      
      this.reviewCycleOptions$ = get(OptionType.reviewCycle, model.reviewCycleId);
  
      this.specialFindingOptions$ = this.store.pipe(
        select(optionSelector.specialFindings),
        map(options => {
          const active = options.filter(o => o.isActive);
          const inactive = options.filter(o =>
            model.specialFindingCategories?.includes(o.id) && !o.isActive
          );
          return [...active, ...inactive].sort((a, b) => a.name.localeCompare(b.name));
        })
      );
    }
  
    private appendSelectedOptionIfDisabled(id: number, options: OptionModel[]): OptionModel[] {
      const selectedOption = options.find(x => x.id === id);
      const activeOptions = options.filter(x => x.isActive === true);
      if (selectedOption && !selectedOption.isActive) {
        activeOptions.push(selectedOption);
      }
      return activeOptions.sort((a, b) => a.name.localeCompare(b.name));
    }
  
    dateFields = [
      { label: 'Submission Date', controlName: 'submissionDate' },
      { label: 'Initial Review Date', controlName: 'initialReview' },
      { label: 'Most Recent Review Date', controlName: 'mostRecentReview' },
      { label: 'Initial Approval Date', controlName: 'initialApprovalDate' },
      { label: 'Approval Through Date', controlName: 'approvalThrough' },
      { label: 'Send Next Review Date', controlName: 'sendNextReview' },
      { label: 'Continuing Review Date', controlName: 'continuingReviewDate' },
      { label: 'Complete Date', controlName: 'completeDate' }
    ];

}
